'use client';
import React, { useState } from 'react';
import { toast } from 'sonner';
import { QROptions } from './QRGeneratorClient';

interface QRActionButtonsProps {
  qrValue: string;
  options: QROptions;
  canvasRef: React.RefObject<HTMLCanvasElement>;
  onGenerateNew: () => void;
}

export default function QRActionButtons({ qrValue, options, onGenerateNew }: QRActionButtonsProps) {
  const [downloading, setDownloading] = useState<string | null>(null);

  const getQRCanvas = (): HTMLCanvasElement | null => {
    return document.querySelector('canvas') as HTMLCanvasElement | null;
  };

  // Helper: generate QR canvas with logo overlaid
  const buildQRCanvasWithLogo = async (size: number): Promise<HTMLCanvasElement> => {
    const QRCode = await import('qrcode');
    const offscreen = document.createElement('canvas');
    offscreen.width = size;
    offscreen.height = size;
    const ctx = offscreen.getContext('2d');
    if (!ctx) throw new Error('Canvas context unavailable');

    if (options.style === 'dots') {
      // Dots style: render each module as a circle
      const qrData = QRCode.create(qrValue, { errorCorrectionLevel: options.errorLevel as 'L' | 'M' | 'Q' | 'H' });
      const modules = qrData.modules;
      const moduleCount = modules.size;
      const quietZoneModules = 2;
      const moduleSize = size / (moduleCount + quietZoneModules * 2);
      const marginPx = quietZoneModules * moduleSize;

      if (options.transparentBg) {
        ctx.clearRect(0, 0, size, size);
      } else {
        ctx.fillStyle = options.bgColor;
        ctx.fillRect(0, 0, size, size);
      }

      ctx.fillStyle = options.fgColor;
      for (let row = 0; row < moduleCount; row++) {
        for (let col = 0; col < moduleCount; col++) {
          if (modules.get(row, col)) {
            const x = marginPx + col * moduleSize;
            const y = marginPx + row * moduleSize;
            const r = moduleSize / 2;
            ctx.beginPath();
            ctx.arc(x + r, y + r, r * 0.85, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      }
    } else {
      await QRCode.toCanvas(offscreen, qrValue, {
        width: size,
        margin: 2,
        color: {
          dark: options.fgColor,
          light: options.transparentBg ? '#00000000' : options.bgColor,
        },
        errorCorrectionLevel: options.errorLevel as 'L' | 'M' | 'Q' | 'H',
      });
    }

    // Overlay logo if present
    if (options.logoUrl) {
      await new Promise<void>((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          const logoSize = Math.round(size * (options.logoSize / 100));
          const logoX = Math.round((size - logoSize) / 2);
          const logoY = Math.round((size - logoSize) / 2);

          // White background circle/square behind logo for readability
          const padding = Math.round(logoSize * 0.15);
          ctx.fillStyle = options.transparentBg ? '#ffffff' : options.bgColor;
          ctx.beginPath();
          ctx.roundRect(
            logoX - padding,
            logoY - padding,
            logoSize + padding * 2,
            logoSize + padding * 2,
            Math.round(padding * 1.2)
          );
          ctx.fill();

          ctx.drawImage(img, logoX, logoY, logoSize, logoSize);
          resolve();
        };
        img.onerror = () => {
          // If logo fails to load, just resolve without it
          resolve();
        };
        img.src = options.logoUrl!;
      });
    }

    return offscreen;
  };

  const downloadPNG = async () => {
    setDownloading('png');
    try {
      const offscreen = await buildQRCanvasWithLogo(options.size);

      offscreen.toBlob(blob => {
        if (!blob) {
          toast.error('Failed to create PNG blob.');
          return;
        }
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `nova-qr-${Date.now()}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success('PNG downloaded successfully!');
        updateDownloadCount();
      }, 'image/png');
    } catch (err) {
      console.error('PNG download error:', err);
      toast.error('Failed to download PNG. Please try again.');
    } finally {
      setDownloading(null);
    }
  };

  const downloadSVG = async () => {
    setDownloading('svg');
    try {
      const QRCode = await import('qrcode');
      const svgString = await QRCode.toString(qrValue, {
        type: 'svg',
        margin: 2,
        color: {
          dark: options.fgColor,
          light: options.transparentBg ? '#00000000' : options.bgColor,
        },
        errorCorrectionLevel: options.errorLevel as 'L' | 'M' | 'Q' | 'H',
        width: options.size,
      });

      let finalSvg = svgString;

      // Embed logo into SVG if present
      if (options.logoUrl) {
        const logoSize = Math.round(options.size * (options.logoSize / 100));
        const logoX = Math.round((options.size - logoSize) / 2);
        const logoY = Math.round((options.size - logoSize) / 2);
        const padding = Math.round(logoSize * 0.15);
        const bgColor = options.transparentBg ? '#ffffff' : options.bgColor;
        const rx = Math.round(padding * 1.2);

        const logoEmbed = `
  <rect x="${logoX - padding}" y="${logoY - padding}" width="${logoSize + padding * 2}" height="${logoSize + padding * 2}" rx="${rx}" ry="${rx}" fill="${bgColor}"/>
  <image href="${options.logoUrl}" x="${logoX}" y="${logoY}" width="${logoSize}" height="${logoSize}" preserveAspectRatio="xMidYMid meet"/>
</svg>`;
        finalSvg = svgString.replace('</svg>', logoEmbed);
      }

      const blob = new Blob([finalSvg], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `nova-qr-${Date.now()}.svg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('SVG downloaded successfully!');
      updateDownloadCount();
    } catch (err) {
      console.error('SVG download error:', err);
      toast.error('Failed to download SVG. Please try again.');
    } finally {
      setDownloading(null);
    }
  };

  const downloadPDF = async () => {
    setDownloading('pdf');
    try {
      const offscreen = await buildQRCanvasWithLogo(options.size);

      const imgData = offscreen.toDataURL('image/png');
      if (!imgData || imgData === 'data:,') throw new Error('Failed to get image data from canvas');

      const { jsPDF } = await import('jspdf');
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const margin = 20;
      const pdfSize = 170;
      pdf.setFillColor(255, 255, 255);
      pdf.rect(0, 0, 210, 297, 'F');
      pdf.addImage(imgData, 'PNG', margin, 40, pdfSize, pdfSize);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(16);
      pdf.setTextColor(15, 23, 42);
      pdf.text('Nova QR Studio', 105, 25, { align: 'center' });
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);
      pdf.setTextColor(100, 116, 139);
      pdf.text('Generated by NovaMax Studios', 105, 32, { align: 'center' });
      const urlText = qrValue.length > 80 ? qrValue.slice(0, 80) + '...' : qrValue;
      pdf.setFontSize(8);
      pdf.text(urlText, 105, 222, { align: 'center' });
      pdf.save(`nova-qr-${Date.now()}.pdf`);
      toast.success('PDF downloaded successfully!');
      updateDownloadCount();
    } catch (err) {
      console.error('PDF download error:', err);
      toast.error('Failed to generate PDF. Please try again.');
    } finally {
      setDownloading(null);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(qrValue);
      toast.success('Link copied to clipboard!');
    } catch {
      toast.error('Failed to copy. Please copy the URL manually.');
    }
  };

  const updateDownloadCount = () => {
    const historyKey = 'nova-qr-history';
    const existing = JSON.parse(localStorage.getItem(historyKey) || '[]');
    if (existing.length > 0) {
      existing[0].downloadCount = (existing[0].downloadCount || 0) + 1;
      localStorage.setItem(historyKey, JSON.stringify(existing));
    }
  };

  return (
    <div className="bg-card rounded-3xl border border-border shadow-card p-4 space-y-3">
      {/* Primary download button — full width */}
      <button
        onClick={downloadPNG}
        disabled={downloading !== null}
        className="btn-primary w-full flex items-center justify-center gap-2.5 disabled:opacity-60 disabled:cursor-not-allowed"
        style={{ padding: '15px 20px', fontSize: '15px', fontWeight: 700, borderRadius: '18px', minHeight: '52px' }}
      >
        {downloading === 'png' ? (
          <svg className="animate-spin" width="18" height="18" viewBox="0 0 18 18" fill="none">
            <circle cx="9" cy="9" r="7" stroke="white" strokeWidth="2" strokeDasharray="22" strokeDashoffset="8" />
          </svg>
        ) : (
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round">
            <path d="M9 11.5V3.5M9 11.5L6 8.5M9 11.5L12 8.5" />
            <path d="M2.5 14.5h13" />
          </svg>
        )}
        Download PNG
      </button>

      {/* Secondary buttons grid */}
      <div className="grid grid-cols-2 gap-2.5">
        <button
          onClick={downloadSVG}
          disabled={downloading !== null}
          className="flex items-center justify-center gap-2 rounded-2xl border border-border font-semibold transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed hover:border-primary/40 hover:bg-secondary"
          style={{ padding: '13px 16px', fontSize: '14px', minHeight: '48px', background: 'var(--card)', color: 'var(--foreground)' }}
        >
          {downloading === 'svg' ? (
            <svg className="animate-spin" width="15" height="15" viewBox="0 0 15 15" fill="none">
              <circle cx="7.5" cy="7.5" r="6" stroke="currentColor" strokeWidth="1.8" strokeDasharray="18" strokeDashoffset="7" />
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
              <path d="M8 10V3M8 10L5.5 7.5M8 10L10.5 7.5" />
              <path d="M2 13h12" />
            </svg>
          )}
          SVG
        </button>

        <button
          onClick={downloadPDF}
          disabled={downloading !== null}
          className="flex items-center justify-center gap-2 rounded-2xl border border-border font-semibold transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed hover:border-primary/40 hover:bg-secondary"
          style={{ padding: '13px 16px', fontSize: '14px', minHeight: '48px', background: 'var(--card)', color: 'var(--foreground)' }}
        >
          {downloading === 'pdf' ? (
            <svg className="animate-spin" width="15" height="15" viewBox="0 0 15 15" fill="none">
              <circle cx="7.5" cy="7.5" r="6" stroke="currentColor" strokeWidth="1.8" strokeDasharray="18" strokeDashoffset="7" />
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
              <rect x="3" y="1.5" width="10" height="13" rx="1.5" />
              <path d="M5.5 5.5h5M5.5 8h5M5.5 10.5h3" />
            </svg>
          )}
          PDF
        </button>

        <button
          onClick={copyLink}
          className="flex items-center justify-center gap-2 rounded-2xl border border-border font-semibold transition-all duration-200 hover:border-primary/40 hover:bg-secondary"
          style={{ padding: '13px 16px', fontSize: '14px', minHeight: '48px', background: 'var(--card)', color: 'var(--foreground)' }}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
            <rect x="4.5" y="4.5" width="9" height="9" rx="1.5" />
            <path d="M2 11V2.5A.5.5 0 0 1 2.5 2H11" />
          </svg>
          Copy Link
        </button>

        <button
          onClick={onGenerateNew}
          className="flex items-center justify-center gap-2 rounded-2xl border border-border font-semibold transition-all duration-200 hover:border-primary/40 hover:bg-secondary"
          style={{ padding: '13px 16px', fontSize: '14px', minHeight: '48px', background: 'var(--card)', color: 'var(--muted-foreground)' }}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
            <path d="M1.5 8A6.5 6.5 0 0 1 13 5M14.5 8A6.5 6.5 0 0 1 3 11" />
            <path d="M11 3l2 2 2-1" />
            <path d="M5 13l-2-2-2 1" />
          </svg>
          New QR
        </button>
      </div>
    </div>
  );
}